import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import VSeeClinicWidget from "@/components/feature/VSeeClinicWidget";
import { adPanelContent, telemedTimeSlots } from "@/mocks/intake-pages";
import { getTotalIntakeSteps } from "@/lib/stage1Questionnaire";
import { startVSeeCall } from "@/lib/vseeApi";
import type { VSeeJoinLinkSession, VSeeSessionConfig } from "@/types/vsee";

type TimeSlot = (typeof telemedTimeSlots)[number];

export default function IntakeVerification() {
  const { phase, goTo, containerClasses } = usePageTransition();
  const totalSteps = getTotalIntakeSteps();
  const [transitioning, setTransitioning] = useState(false);
  const [checkmarks, setCheckmarks] = useState<number>(0);
  const [showScheduling, setShowScheduling] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [vseeSession, setVseeSession] = useState<
    VSeeSessionConfig | VSeeJoinLinkSession | null
  >(null);
  const [vseeStarting, setVseeStarting] = useState(false);
  const [vseeError, setVseeError] = useState<string | null>(null);

  useEffect(() => {
    const timers = [400, 900, 1400, 1900, 2500];
    timers.forEach((delay, i) => {
      setTimeout(() => {
        setCheckmarks((prev) => Math.max(prev, i + 1));
      }, delay);
    });
  }, []);

  const verificationItems = [
    { icon: "ri-file-text-line", label: "Intake questionnaire compiled" },
    { icon: "ri-brain-line", label: "K-Level functional mobility assessed" },
    { icon: "ri-shield-check-line", label: "Insurance coverage analyzed" },
    { icon: "ri-user-add-line", label: "Account created & profile saved" },
    { icon: "ri-smartphone-line", label: "Care team coordination initiated" },
  ];

  const handleOpenScheduling = () => {
    setShowScheduling(true);
  };

  const handleSelectSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
  };

  const handleConfirmBooking = () => {
    setBookingConfirmed(true);
    setTransitioning(true);
    setTimeout(() => {
      goTo("/telehealth-consultation");
    }, 800);
  };

  const handleBackToSlots = () => {
    setSelectedSlot(null);
  };

  const handleCallNow = async () => {
    setVseeStarting(true);
    setVseeError(null);
    try {
      setVseeSession(await startVSeeCall());
    } catch (err) {
      setVseeError(
        err instanceof Error ? err.message : "Failed to start the call.",
      );
    } finally {
      setVseeStarting(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      <IntakeTransitionOverlay />
      {/* Top Bar: Back + Progress */}
      <div className="flex-shrink-0 bg-white border-b border-background-100">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-4">
            <Link
              to="/intake/myfit-scan"
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back
            </Link>
          </div>

          <div className="pb-4">
            <IntakeProgressBar
              currentStep={totalSteps}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-500 ease-out relative ${containerClasses}`}
      >
        {/* Warm Background Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-primary-100/12 blur-3xl"></div>
        </div>

        <div className="w-full max-w-[1100px] mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">
            {/* Left: Relief State Panel */}
            <div className="lg:w-[55%] flex flex-col justify-center">
              <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 animate-fadeSlideIn">
                {/* Aspirational Lifestyle Photo — "This is what you just unlocked" */}
                <div className="mb-7 rounded-xl overflow-hidden relative min-h-[180px] md:min-h-[200px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Amputee%20person%20walking%20confidently%20along%20a%20quiet%20tree%20lined%20neighborhood%20sidewalk%20on%20a%20bright%20sunny%20morning%2C%20wearing%20casual%20modern%20clothing%2C%20prosthetic%20leg%20visible%20but%20natural%20part%20of%20their%20movement%2C%20warm%20uplifting%20golden%20morning%20light%2C%20sense%20of%20freedom%20and%20independence%2C%20clean%20suburban%20street%20slightly%20blurred%20in%20background%2C%20editorial%20lifestyle%20photography%2C%20warm%20cream%20and%20soft%20sage%20green%20tones%2C%20hopeful%20optimistic%20atmosphere%2C%20no%20clinical%20setting%2C%20pure%20outdoor%20freedom%20moment&width=800&height=400&seq=intake-verification-lifestyle&orientation=landscape"
                    alt="Walking freely with a prosthetic — this journey leads to real mobility"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-[10px] md:text-[11px] font-semibold text-foreground-800">
                      <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                        <i className="ri-walk-line text-[10px]"></i>
                      </span>
                      One step closer to walking on your terms
                    </span>
                  </div>
                </div>

                {/* Success Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="w-8 h-8 flex items-center justify-center text-green-600">
                    <i className="ri-check-double-line text-2xl"></i>
                  </span>
                </div>

                {/* Relief Messaging */}
                <div className="text-center mb-8">
                  <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                    The hard part is over!
                  </h1>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-lg mx-auto">
                    We have all the information needed to share with your
                    physician and certified prosthetist to complete your medical
                    evaluation.
                  </p>
                </div>

                {/* Animated Verification Checklist */}
                <div className="space-y-2.5 mb-7">
                  {verificationItems.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                        checkmarks > i
                          ? "bg-green-50 border border-green-100"
                          : "bg-background-50 border border-background-100"
                      }`}
                    >
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                          checkmarks > i
                            ? "bg-green-500 text-background-50"
                            : "bg-background-200 text-foreground-400"
                        }`}
                      >
                        {checkmarks > i ? (
                          <i className="ri-check-line text-sm"></i>
                        ) : (
                          <i className="ri-loader-4-line text-sm animate-spin"></i>
                        )}
                      </span>
                      <span
                        className={`text-xs md:text-sm font-medium transition-colors ${
                          checkmarks > i
                            ? "text-green-700"
                            : "text-foreground-500"
                        }`}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Scheduling / Telehealth Prompt */}
                {vseeSession ? (
                  "joinUrl" in vseeSession ? (
                    createPortal(
                      <div className="fixed inset-0 z-[999] bg-foreground-950 flex flex-col">
                        <div className="flex-shrink-0 flex items-center justify-end px-4 py-2.5 bg-foreground-900">
                          <button
                            type="button"
                            onClick={() => setVseeSession(null)}
                            aria-label="Close"
                            className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-base"></i>
                          </button>
                        </div>
                        <iframe
                          src={vseeSession.joinUrl}
                          className="flex-1 min-h-0"
                          allow="camera; microphone; fullscreen; display-capture"
                        />
                      </div>,
                      document.body,
                    )
                  ) : (
                    <VSeeClinicWidget
                      session={vseeSession}
                      onLeave={() => setVseeSession(null)}
                    />
                  )
                ) : !showScheduling ? (
                  <>
                    {/* Telehealth Prompt */}
                    <div className="rounded-2xl border-2 border-primary-200 bg-primary-50/50 p-6 md:p-7 mb-6">
                      <div className="flex items-start gap-4">
                        <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-vidicon-line text-lg"></i>
                        </span>
                        <div>
                          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-2">
                            Skip the 4-to-5 week wait at a traditional clinic
                          </h3>
                          <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                            Once your scans are submitted, connect instantly
                            with a partner physician to review your evaluation,
                            complete your medical assessment, and sign off on
                            your prescription.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <button
                      type="button"
                      onClick={handleOpenScheduling}
                      className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-calendar-check-line text-sm"></i>
                      </span>
                      Schedule Telemed Call Now
                    </button>

                    <button
                      type="button"
                      onClick={handleCallNow}
                      disabled={vseeStarting}
                      className="w-full mt-3 py-3.5 rounded-xl border-2 border-primary-500 text-primary-600 text-sm md:text-base font-bold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i
                          className={
                            vseeStarting
                              ? "ri-loader-4-line text-sm animate-spin"
                              : "ri-phone-line text-sm"
                          }
                        ></i>
                      </span>
                      {vseeStarting
                        ? "Starting call…"
                        : "Call Now via VSee Clinic"}
                    </button>

                    {vseeError && (
                      <p className="text-xs text-red-600 text-center mt-2">
                        {vseeError}
                      </p>
                    )}
                  </>
                ) : !selectedSlot ? (
                  /* ---------- TIME SLOT SELECTION ---------- */
                  <div className="animate-fadeSlideIn">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-calendar-line text-base"></i>
                      </span>
                      <div>
                        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                          Choose Your Time Slot
                        </h3>
                        <p className="text-[10px] md:text-xs text-foreground-500">
                          Select an available time for your telehealth
                          consultation
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 mb-5 max-h-[280px] overflow-y-auto pr-1">
                      {telemedTimeSlots.map((slot) => (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => handleSelectSlot(slot)}
                          className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer group"
                        >
                          <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                            <i className="ri-calendar-event-line text-sm"></i>
                          </span>
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-bold text-foreground-900 whitespace-nowrap">
                              {slot.date} at {slot.time}
                            </p>
                            <p className="text-[10px] text-foreground-500 whitespace-nowrap">
                              {slot.duration} · {slot.physician}
                            </p>
                          </div>
                          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-300 group-hover:text-primary-500 transition-colors">
                            <i className="ri-arrow-right-s-line text-sm"></i>
                          </span>
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowScheduling(false)}
                      className="w-full py-3 rounded-xl border border-background-200 bg-background-50 text-xs md:text-sm font-semibold text-foreground-500 hover:text-foreground-800 hover:border-background-300 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Cancel — Go Back
                    </button>
                  </div>
                ) : !bookingConfirmed ? (
                  /* ---------- SLOT CONFIRMATION ---------- */
                  <div className="animate-fadeSlideIn">
                    <div className="rounded-2xl border-2 border-green-200 bg-green-50/50 p-6 md:p-7 mb-5">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-100 text-green-600">
                          <i className="ri-calendar-check-line text-base"></i>
                        </span>
                        <div>
                          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                            Confirm Your Appointment
                          </h3>
                          <p className="text-[10px] md:text-xs text-foreground-500">
                            Review the details below before confirming
                          </p>
                        </div>
                      </div>

                      <div className="rounded-xl bg-white border border-green-100 p-4 space-y-2.5 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">
                            Date &amp; Time
                          </span>
                          <span className="text-xs font-bold text-foreground-900">
                            {selectedSlot.date} at {selectedSlot.time}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">
                            Duration
                          </span>
                          <span className="text-xs font-bold text-foreground-900">
                            {selectedSlot.duration}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">
                            Physician
                          </span>
                          <span className="text-xs font-bold text-foreground-900">
                            {selectedSlot.physician}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">
                            Type
                          </span>
                          <span className="text-xs font-bold text-foreground-900">
                            Video Call (Telehealth)
                          </span>
                        </div>
                      </div>

                      <p className="text-[10px] text-foreground-500 leading-relaxed">
                        You&apos;ll receive a confirmation link via email. You
                        can reschedule or cancel up to 2 hours before the
                        appointment.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleBackToSlots}
                        className="flex-1 py-3.5 rounded-xl border border-background-200 bg-background-50 text-xs md:text-sm font-semibold text-foreground-500 hover:text-foreground-800 hover:border-background-300 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Pick a different time
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmBooking}
                        className="flex-[2] py-3.5 rounded-xl bg-primary-500 text-background-50 text-xs md:text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-xs"></i>
                        </span>
                        Confirm &amp; Join Call
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ---------- BOOKING CONFIRMED ---------- */
                  <div className="w-full py-4 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center gap-3 animate-fadeSlideIn">
                    <span className="w-5 h-5 flex items-center justify-center text-green-600">
                      <i className="ri-loader-4-line text-sm animate-spin"></i>
                    </span>
                    <span className="text-sm font-bold text-green-700">
                      Booking confirmed — connecting you now...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Ad Panel */}
            <div className="lg:w-[45%] flex">
              <div className="bg-white rounded-2xl border border-background-100 p-6 md:p-8 flex flex-col w-full animate-slideUpIn">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4 self-start">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-play-circle-line text-[9px]"></i>
                  </span>
                  What&apos;s Next
                </span>

                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
                  {adPanelContent.verificationRelief.title}
                </h3>

                <div className="rounded-xl overflow-hidden mb-4 flex-shrink-0">
                  <img
                    src="https://www.instagram.com/reels/DYXfH66FfP9/"
                    alt="What happens next after intake"
                    className="w-full h-auto"
                  />
                </div>

                <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                  {adPanelContent.verificationRelief.subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Footer */}
      <div className="w-full border-t border-background-100 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[10px]"></i>
            </span>
            <p className="text-[10px] text-foreground-400 leading-relaxed max-w-3xl">
              Notice: This platform is an administrative optimization tool. All
              AI-generated functional assessments, financial forecasts,
              wholesale cost aggregations, advertising recommendations, 3D
              metric visualizations, and documentation templates are strictly
              informational. Final clinical specifications, billing submittals,
              prescriptions, and custom fabrication workflows require formal,
              independent review and explicit sign-off by a licensed physician
              and a Certified Prosthetist prior to patient care.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
